import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Alert, ProgressBar } from 'react-bootstrap';
import { AuthContext } from '../../contexts/AuthContext';
import { Clock, CheckCircle, Calendar, Mail, Phone, ExternalLink, AlertTriangle, Pencil, PauseCircle } from 'lucide-react';
import { verificationReasonCopy } from '../../constants/verificationReasonCodes';

/**
 * Human-readable labels for the `needsInfoFields` dot-paths the admin can flag.
 * Keep in sync with NEEDS_INFO_FIELD_OPTIONS in AdminGroupsList.jsx.
 */
const FIELD_LABELS = {
  name: 'Company name',
  website: 'Website',
  description: 'Description / About',
  'contact.name': 'Contact person (name)',
  'contact.email': 'Contact email',
  'contact.phone': 'Contact phone',
  facilityIds: 'Facilities (add/update)',
};

const CALENDLY_BASE_URL =
  process.env.REACT_APP_CALENDLY_URL ||
  'https://calendly.com/rx-rooster/30min';

/**
 * Review status configuration
 */
const REVIEW_STATUS_INFO = {
  pending: {
    title: 'Account Pending Review',
    variant: 'warning',
    icon: Clock,
    description: 'Your account is in the queue for review.',
    timeline: 'Usually 2-3 business days',
  },
  in_review: {
    title: 'Account Under Review',
    variant: 'info',
    icon: Clock,
    description: 'Our team is actively reviewing your account.',
    timeline: 'Usually 1-2 business days',
  },
  needs_info: {
    title: 'Additional Information Required',
    variant: 'warning',
    icon: Clock,
    description: 'We need more information to complete your review.',
    timeline: 'Review will resume once information is provided',
  },
  rejected: {
    title: 'Schedule a Call to Continue',
    variant: 'danger',
    icon: Clock,
    description:
      'Your account was not approved. The only way to move forward is to schedule a call with our team.',
    timeline: null,
  },
  // D1.2.4. Suspension is orthogonal to review status: `GroupApprovedGuard`
  // answers 403 with `reviewStatus: 'suspended'` and `code: 'GROUP_SUSPENDED'`,
  // so this modal has to have an entry for a value the GroupReviewStatus enum
  // does not contain. Without one it fell through to `pending` and told a
  // suspended organization it was in a queue.
  suspended: {
    title: 'Your Rx Rooster account is paused',
    variant: 'danger',
    icon: PauseCircle,
    description: 'This account is paused while we review it.',
    timeline: null,
  },
};

/**
 * Modal component to handle when groups under review try to publish listings
 * Shows a user-friendly message and provides a Calendly scheduling option
 */
const UnderReviewModal = () => {
  const { auth } = useContext(AuthContext);
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState('');
  const [reviewStatus, setReviewStatus] = useState('pending');
  const [reviewReason, setReviewReason] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [suspensionReasonCode, setSuspensionReasonCode] = useState('');
  const [needsInfoFields, setNeedsInfoFields] = useState([]);
  const [showCalendly, setShowCalendly] = useState(false);

  useEffect(() => {
    const handleShowModal = (event) => {
      const status = event.detail.reviewStatus || 'pending';
      // Respect the per-session dismissal key (set by handleClose / the
      // "Update Company Details" button). Without this check, any downstream
      // dispatcher — axios interceptors, background retries — would force the
      // modal back open after the user navigated to /company/settings.
      // Rejected groups cannot dismiss, so we never suppress for them.
      if (status !== 'rejected') {
        try {
          const group = auth?.user?.group;
          const gid = group?._id || group?.id;
          if (gid && sessionStorage.getItem(`underReviewDismissed:${gid}:${status}`) === '1') {
            return;
          }
        } catch (e) { /* fall through and show */ }
      }
      setMessage(event.detail.message);
      setReviewStatus(status);
      setReviewReason(event.detail.reviewReason || '');
      setReviewNotes(event.detail.reviewNotes || '');
      setSuspensionReasonCode(event.detail.suspensionReasonCode || '');
      setNeedsInfoFields(event.detail.needsInfoFields || []);
      // For rejected groups, skip straight to Calendly — scheduling a call is the
      // only action available to them.
      setShowCalendly(status === 'rejected');
      setShow(true);
    };

    window.addEventListener('showUnderReviewModal', handleShowModal);

    return () => {
      window.removeEventListener('showUnderReviewModal', handleShowModal);
    };
  }, [auth?.user?.group?._id, auth?.user?.group?.id]);

  // Proactively surface modal for actionable review states (needs_info, rejected)
  // so the group sees the admin's request without having to attempt a gated action.
  useEffect(() => {
    const group = auth?.user?.group;
    if (!group) return;
    const status = group.reviewStatus;
    if (status === 'approved' || !status) return;
    // Suppress if user already dismissed this status in this session
    const dismissKey = `underReviewDismissed:${group._id || group.id}:${status}`;
    if (status !== 'rejected' && sessionStorage.getItem(dismissKey) === '1') return;

    const message =
      status === 'needs_info'
        ? 'Please review and correct the information flagged by our team.'
        : status === 'rejected'
        ? 'Your account was not approved. Please schedule a call with our team.'
        : 'Your account is under review. Most features are unavailable until approval.';

    window.dispatchEvent(
      new CustomEvent('showUnderReviewModal', {
        detail: {
          message,
          reviewStatus: status,
          reviewReason: group.reviewReason || '',
          reviewNotes: group.reviewNotes || '',
          needsInfoFields: group.needsInfoFields || [],
        },
      }),
    );
  }, [auth?.user?.group?.reviewStatus, auth?.user?.group?._id]);

  // Get status info
  const statusInfo = REVIEW_STATUS_INFO[reviewStatus] || REVIEW_STATUS_INFO.pending;

  // Load Calendly script and initialize widget when showing Calendly
  useEffect(() => {
    if (showCalendly) {
      // Check if Calendly is already loaded
      if (window.Calendly) {
        initializeCalendlyWidget();
        return;
      }

      // Load Calendly script if not already present
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');

      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        script.onload = () => {
          initializeCalendlyWidget();
        };
        document.head.appendChild(script);
      } else {
        // Script exists but Calendly might not be loaded yet
        const checkCalendly = setInterval(() => {
          if (window.Calendly) {
            clearInterval(checkCalendly);
            initializeCalendlyWidget();
          }
        }, 100);

        // Clear interval after 5 seconds to avoid infinite checking
        setTimeout(() => clearInterval(checkCalendly), 5000);
      }
    }
  }, [showCalendly]);

  // Initialize Calendly widget programmatically
  const initializeCalendlyWidget = () => {
    const { name, email } = getUserDetails();
    const calendlyElement = document.querySelector('.calendly-inline-widget');

    if (calendlyElement && window.Calendly) {
      // Clear any existing content
      calendlyElement.innerHTML = '';

      // Initialize Calendly widget
      window.Calendly.initInlineWidget({
        url: `${CALENDLY_BASE_URL}?name=${name ? encodeURIComponent(name) : ''}&email=${email ? encodeURIComponent(email) : ''}`,
        parentElement: calendlyElement,
        prefill: {
          name: name,
          email: email
        }
      });
    }
  };

  const handleClose = () => {
    // Remember dismissal so proactive mount-effect doesn't re-pop this session.
    const group = auth?.user?.group;
    if (group && reviewStatus && reviewStatus !== 'rejected') {
      try {
        sessionStorage.setItem(
          `underReviewDismissed:${group._id || group.id}:${reviewStatus}`,
          '1',
        );
      } catch (e) { /* ignore */ }
    }
    setShow(false);
    setShowCalendly(false);
  };

  const handleScheduleCall = () => {
    setShowCalendly(true);
  };

  // Get user details for Calendly pre-population
  const getUserDetails = () => {
    // For GROUP users, the data is in auth.user.group.contact
    const name = auth?.user?.group?.contact?.name || '';
    const email = auth?.user?.group?.contact?.email || '';

    console.log('Extracted name:', name);
    console.log('Extracted email:', email);

    return { name, email };
  };

  // Generate Calendly URL with pre-populated user details
  const getCalendlyUrl = () => {
    const { name, email } = getUserDetails();
    return `${CALENDLY_BASE_URL}?name=${name ? encodeURIComponent(name) : ''}&email=${email ? encodeURIComponent(email) : ''}`;
  };

  const StatusIcon = statusInfo.icon;

  const isRejected = reviewStatus === 'rejected';

  return (
    <Modal
      show={show}
      // Rejected groups cannot dismiss — scheduling a call is the only path forward.
      onHide={isRejected ? undefined : handleClose}
      backdrop={isRejected ? 'static' : true}
      keyboard={!isRejected}
      size="lg"
      centered
    >
      <Modal.Header
        closeButton={!isRejected}
        style={{ borderBottom: 'none', paddingBottom: 0 }}
      >
        <Modal.Title className="d-flex align-items-center gap-2">
          <StatusIcon size={24} className={`text-${statusInfo.variant}`} />
          {statusInfo.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!showCalendly ? (
          <>
            {/* Status summary */}
            <Alert variant={statusInfo.variant} className="mb-3">
              <div className="d-flex align-items-start gap-2">
                <div>
                  <strong>{statusInfo.description}</strong>
                  {message && <p className="mb-0 mt-1">{message}</p>}
                </div>
              </div>
            </Alert>

            {/* The remediation line for the suspension reason code (D7.1). It
                names the next move, which "your account is paused" does not. */}
            {reviewStatus === 'suspended' && (
              <Alert variant="light" className="mb-3 border">
                <p className="mb-0">{verificationReasonCopy(suspensionReasonCode)}</p>
              </Alert>
            )}

            {/* Admin-provided message (reviewReason or reviewNotes) */}
            {(reviewReason || reviewNotes) && (
              <Alert variant="light" className="mb-3 border">
                <div className="d-flex align-items-start gap-2">
                  <AlertTriangle size={18} className="mt-1 text-warning flex-shrink-0" />
                  <div>
                    <strong>Message from the RxRooster review team</strong>
                    {reviewReason && <p className="mb-0 mt-1">{reviewReason}</p>}
                    {reviewNotes && reviewNotes !== reviewReason && (
                      <p className="mb-0 mt-2 fst-italic text-muted">{reviewNotes}</p>
                    )}
                  </div>
                </div>
              </Alert>
            )}

            {/* Fields the admin flagged for correction */}
            {needsInfoFields.length > 0 && (
              <div className="mb-3 p-3 border rounded" style={{ background: '#fff8e5', borderColor: '#f0ad4e' }}>
                <strong className="d-block mb-2">Fields you need to update:</strong>
                <ul className="mb-0 ps-3">
                  {needsInfoFields.map((f) => (
                    <li key={f}>{FIELD_LABELS[f] || f}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Timeline (only when admin hasn't flagged specific fields) */}
            {statusInfo.timeline && needsInfoFields.length === 0 && (
              <div className="d-flex align-items-center gap-2 mb-3 p-3 bg-light rounded">
                <Clock size={18} className="text-muted" />
                <div>
                  <strong>Estimated Timeline:</strong> {statusInfo.timeline}
                </div>
              </div>
            )}

            {/* Contact options */}
            <div className="text-center text-muted small">
              <p className="mb-1">Questions? We're here to help.</p>
              <a
                href="mailto:support@rxrooster.com"
                className="text-decoration-none d-inline-flex align-items-center gap-1"
              >
                <Mail size={14} />
                <span>support@rxrooster.com</span>
              </a>
            </div>
          </>
        ) : (
          <div className="calendly-container">
            <div className="text-center mb-3">
              <h5>Schedule Your Account Review Call</h5>
              <p className="text-muted">
                Choose a convenient time to speak with our team. This call typically takes 10 minutes.
              </p>
            </div>

            {/* Calendly Inline Widget */}
            <div
              className="calendly-inline-widget"
              style={{ minWidth: '320px', height: '500px' }}
            ></div>

            <div className="text-center mt-3">
              <small className="text-muted">
                Having trouble with the scheduler? Email us at{' '}
                <a href="mailto:support@rxrooster.com">support@rxrooster.com</a>
              </small>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: showCalendly ? '1px solid #dee2e6' : 'none' }}>
        {!showCalendly ? (
          <div className="d-flex gap-2 w-100 justify-content-end flex-wrap">
            {/* Schedule-a-call is always available as an escalation path. */}
            <Button
              variant="outline-primary"
              onClick={handleScheduleCall}
              className="d-inline-flex align-items-center gap-2"
            >
              <Calendar size={16} />
              <span>Schedule a Call</span>
            </Button>
            {/* Primary action: go fix the flagged issues in settings. */}
            <Button
              variant="warning"
              onClick={() => {
                handleClose();
                const qs = needsInfoFields.length > 0
                  ? '?section=company&highlight=needsInfo'
                  : '?section=company';
                window.location.href = `/company/settings${qs}`;
              }}
              className="d-inline-flex align-items-center gap-2"
            >
              <Pencil size={16} />
              <span>Update Company Details →</span>
            </Button>
          </div>
        ) : (
          <>
            {/* Rejected groups have no "Back" — Calendly is the only option. */}
            {!isRejected && (
              <Button variant="outline-secondary" onClick={() => setShowCalendly(false)}>
                Back
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default UnderReviewModal;
